"use client";

import { useState, useRef, useEffect } from "react";
import "../assets/css/otp.min.css";

export default function ForgotPasswordPage () {
    const [currentStep, setCurrentStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState(["", "", "", ""]);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [timer, setTimer] = useState(60);
    const [canResend, setCanResend] = useState(false);

    // OTP input refs
    const otpRefs = [useRef(), useRef(), useRef(), useRef()];

    // Static OTP for verification
    const STATIC_OTP = "1234";

    // Timer effect
    useEffect(() => {
        let interval = null;
        if (currentStep === 2 && timer > 0) {
            interval = setInterval(() => {
                setTimer((timer) => timer - 1);
            }, 1000);
        } else if (timer === 0) {
            setCanResend(true);
        }
        return () => clearInterval(interval);
    }, [currentStep, timer]);

    // Handle email submission
    const handleEmailSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true);

        // Simulate API call
        setTimeout(() => {
            setIsLoading(false);
            setCurrentStep(2);
            setTimer(60);
            setCanResend(false);
            alert(`OTP sent to ${email}`);
        }, 1500);
    };

    // Handle OTP input change
    const handleOtpChange = (index, value) => {
        if (value.length > 1) return; // Prevent multiple characters

        const newOtp = [...otp];
        newOtp[index] = value;

        setOtp(newOtp);

        // Auto-focus next input
        if (value && index < 3) {
            otpRefs[index + 1].current.focus();
        }
    };

    // Handle OTP key down
    const handleOtpKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            otpRefs[index - 1].current.focus();
        }
    };

    // Handle OTP verification
    const handleOtpSubmit = (e) => {
        e.preventDefault();
        const enteredOtp = otp.join("");

        if (enteredOtp.length !== 4) {
            alert("Please enter complete OTP");
            return;
        }

        setIsLoading(true);

        // Verify OTP
        setTimeout(() => {
            setIsLoading(false);
            if (enteredOtp === STATIC_OTP) {
                setCurrentStep(3);
                alert("OTP verified successfully!");
            } else {
                alert("Invalid OTP. Please try again.");
                setOtp(["", "", "", ""]);
                otpRefs[0].current.focus();
            }
        }, 1000);
    };

    // Handle password reset
    const handlePasswordReset = (e) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            alert("Passwords do not match!");
            return;
        }

        if (newPassword.length < 6) {
            alert("Password must be at least 6 characters long!");
            return;
        }

        setIsLoading(true);

        // Simulate password reset
        setTimeout(() => {
            setIsLoading(false);
            alert("Password reset successfully!");
            // Reset form
            setCurrentStep(1);
            setEmail("");
            setOtp(["", "", "", ""]);
            setNewPassword("");
            setConfirmPassword("");
        }, 1500);
    };

    // Handle resend OTP
    const handleResendOtp = () => {
        setTimer(60);
        setCanResend(false);
        setOtp(["", "", "", ""]);
        otpRefs[0].current.focus();
        alert("New OTP sent!");
    };

    // Go back to previous step
    const handleGoBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    return (
        <div className="forgot-password-wrapper">
            <div className="full-container">
                <div className="forgot-password-container">
                    {/* Header */}
                    <div className="forgot-header">
                        <div className="step-indicator">
                            <div className={`step ${currentStep >= 1 ? "active" : ""}`}>1</div>
                            <div className="step-line"></div>
                            <div className={`step ${currentStep >= 2 ? "active" : ""}`}>2</div>
                            <div className="step-line"></div>
                            <div className={`step ${currentStep >= 3 ? "active" : ""}`}>3</div>
                        </div>
                    </div>

                    {/* Step 1: Email Input */}
                    {currentStep === 1 && (
                        <div className="step-content">
                            <div className="step-header">
                                <h2 className="step-title">Forgot Password?</h2>
                                <p className="step-subtitle">Enter your email address to receive OTP</p>
                            </div>

                            <form onSubmit={handleEmailSubmit} className="forgot-form">
                                <div className="input-group">
                                    <label htmlFor="email" className="field-label">
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        className="input-field"
                                        placeholder="Enter your email address"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                </div>

                                <button type="submit" className="button button-primary full-width" disabled={isLoading}>
                                    {isLoading ? "Sending OTP..." : "Send OTP"}
                                </button>

                                <div className="form-footer">
                                    <a href="/" className="link-text">
                                        ← Back to Login
                                    </a>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Step 2: OTP Verification */}
                    {currentStep === 2 && (
                        <div className="step-content">
                            <div className="step-header">
                                <h2 className="step-title">Enter OTP</h2>
                                <p className="step-subtitle">
                                    We've sent a 4-digit code to <strong>{email}</strong>
                                </p>
                                <p className="otp-hint">
                                    Current OTP: <strong>1234</strong>
                                </p>
                            </div>

                            <form onSubmit={handleOtpSubmit} className="forgot-form">
                                <div className="otp-container">
                                    <label className="field-label">Enter 4-Digit OTP</label>
                                    <div className="otp-inputs">
                                        {otp.map((digit, index) => (
                                            <input
                                                key={index}
                                                ref={otpRefs[index]}
                                                type="text"
                                                maxLength="1"
                                                className="otp-input"
                                                value={digit}
                                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div className="timer-section">
                                    {!canResend ? (
                                        <p className="timer-text">Resend OTP in {timer}s</p>
                                    ) : (
                                        <button type="button" className="resend-button" onClick={handleResendOtp}>
                                            Resend OTP
                                        </button>
                                    )}
                                </div>

                                <button type="submit" className="button button-primary full-width" disabled={isLoading}>
                                    {isLoading ? "Verifying..." : "Verify OTP"}
                                </button>

                                <div className="form-footer">
                                    <button type="button" className="link-text" onClick={handleGoBack}>
                                        ← Change Email
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Step 3: New Password */}
                    {currentStep === 3 && (
                        <div className="step-content">
                            <div className="step-header">
                                <h2 className="step-title">Reset Password</h2>
                                <p className="step-subtitle">Create a new password for your account</p>
                            </div>

                            <form onSubmit={handlePasswordReset} className="forgot-form">
                                <div className="input-group">
                                    <label htmlFor="newPassword" className="field-label">
                                        New Password
                                    </label>
                                    <input
                                        type="password"
                                        id="newPassword"
                                        name="newPassword"
                                        className="input-field"
                                        placeholder="Enter new password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="input-group">
                                    <label htmlFor="confirmPassword" className="field-label">
                                        Confirm Password
                                    </label>
                                    <input
                                        type="password"
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        className="input-field"
                                        placeholder="Confirm new password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                    />
                                </div>

                                <button type="submit" className="button button-success full-width" disabled={isLoading}>
                                    {isLoading ? "Resetting..." : "Reset Password"}
                                </button>

                                <div className="form-footer">
                                    <button type="button" className="link-text" onClick={handleGoBack}>
                                        ← Back to OTP
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
